import { useState, useRef } from "react";

const tiers = [
  {
    level: 1,
    name: "Tier 1",
    label: "Basic",
    status: "completed",
    description: "Phone & email verified",
    dailyLimit: "₦50,000",
    monthlyLimit: "₦200,000",
    docs: [
      { name: "Phone Number", status: "verified", icon: "ri-smartphone-line" },
      { name: "Email Address", status: "verified", icon: "ri-mail-check-line" },
      { name: "BVN Verification", status: "verified", icon: "ri-bank-card-line" },
    ],
  },
  {
    level: 2,
    name: "Tier 2",
    label: "Standard",
    status: "completed",
    description: "Government ID submitted",
    dailyLimit: "₦500,000",
    monthlyLimit: "₦2,000,000",
    docs: [
      { name: "National ID (NIN)", status: "verified", icon: "ri-id-card-line" },
      { name: "Selfie / Liveness Check", status: "verified", icon: "ri-camera-line" },
      { name: "Proof of Address", status: "verified", icon: "ri-home-4-line" },
    ],
  },
  {
    level: 3,
    name: "Tier 3",
    label: "Premium",
    status: "pending",
    description: "Enhanced due diligence",
    dailyLimit: "₦5,000,000",
    monthlyLimit: "Unlimited",
    docs: [
      { name: "Utility Bill (last 3 months)", status: "pending", icon: "ri-file-text-line" },
      { name: "Source of Funds Declaration", status: "not_submitted", icon: "ri-money-dollar-circle-line" },
    ],
  },
];

const statusConfig = {
  verified: { label: "Verified", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100", icon: "ri-checkbox-circle-fill" },
  pending: { label: "Pending", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100", icon: "ri-time-line" },
  not_submitted: { label: "Not Submitted", color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-100", icon: "ri-circle-line" },
  completed: { label: "Completed", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100", icon: "ri-checkbox-circle-fill" },
};

export default function KYCModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("status");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeStep, setUpgradeStep] = useState(1);
  const [utilityBillFile, setUtilityBillFile] = useState(null);
  const [utilityBillPreview, setUtilityBillPreview] = useState(null);
  const [sourceOfFunds, setSourceOfFunds] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [employer, setEmployer] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const currentTier = 2;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUtilityBillFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setUtilityBillPreview(ev.target?.result);
      reader.readAsDataURL(file);
    } else {
      setUtilityBillPreview(null);
    }
  };

  const handleUpgradeSubmit = async () => {
    setSubmitStatus("submitting");
    try {
      const params = new URLSearchParams();
      params.append("employment_type", employmentType);
      params.append("monthly_income", monthlyIncome);
      params.append("employer", employer);
      params.append("source_of_funds", sourceOfFunds);
      params.append("utility_bill", utilityBillFile ? utilityBillFile.name : "Uncollectable");

      const res = await fetch("https://readdy.ai/api/form/d7bv83aqofe8vli40r4g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  const resetUpgrade = () => {
    setShowUpgrade(false);
    setUpgradeStep(1);
    setUtilityBillFile(null);
    setUtilityBillPreview(null);
    setSourceOfFunds("");
    setEmploymentType("");
    setMonthlyIncome("");
    setEmployer("");
    setSubmitStatus("idle");
  };

  const canProceedStep1 = utilityBillFile !== null;
  const canProceedStep2 = employmentType !== "" && monthlyIncome !== "" && sourceOfFunds.trim().length >= 10;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Main Sheet */}
      <div
        className="relative bg-white rounded-t-3xl w-full max-h-[92vh] flex flex-col"
        style={{ animation: "slideUp 0.3s ease-out" }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-slate-600 text-lg" />
            </button>
            <div>
              <h2 className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                KYC Verification
              </h2>
              <p className="text-slate-400 text-xs">Identity verification status</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full">
            <i className="ri-shield-check-fill text-emerald-600 text-sm" />
            <span className="text-emerald-600 text-xs font-bold">Tier 2 Verified</span>
          </div>
        </div>

        {/* Hero status card */}
        <div className="mx-5 mt-4 rounded-2xl overflow-hidden flex-shrink-0" style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 100%)" }}>
          <div className="px-5 py-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-emerald-200 text-xs font-medium mb-1">Current Level</p>
                <p className="text-white font-bold text-2xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Tier 2 — Standard</p>
                <p className="text-emerald-300 text-xs mt-0.5">Fully verified · Active since Mar 2026</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                <i className="ri-shield-star-fill text-white text-3xl" />
              </div>
            </div>

            {/* Tier progress bar */}
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map((t) => (
                <div key={t} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`h-1.5 w-full rounded-full ${
                      t <= currentTier ? "bg-emerald-400" : "bg-white/20"
                    }`}
                  />
                  <span className={`text-xs font-medium ${t <= currentTier ? "text-emerald-300" : "text-white/40"}`}>
                    T{t}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mx-5 mt-4 bg-slate-100 rounded-xl p-1 flex-shrink-0">
          {(["status", "limits"]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab === "status" ? "Verification Steps" : "Transaction Limits"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 pb-8">
          {activeTab === "status" ? (
            <div className="space-y-4">
              {tiers.map((tier) => {
                const tierStatus = statusConfig[tier.status];
                const isActive = tier.level === currentTier;
                const isLocked = tier.level > currentTier + 1;

                return (
                  <div
                    key={tier.level}
                    className={`rounded-2xl border overflow-hidden ${
                      isActive
                        ? "border-emerald-200 bg-emerald-50/50"
                        : "border-slate-100 bg-white"
                    }`}
                  >
                    {/* Tier header */}
                    <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            tier.status === "completed"
                              ? "bg-emerald-100"
                              : tier.status === "pending"
                              ? "bg-amber-100"
                              : "bg-slate-100"
                          }`}
                        >
                          <i
                            className={`${tierStatus.icon} text-lg ${tierStatus.color}`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-slate-800 text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                              {tier.name}
                            </p>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tierStatus.bg} ${tierStatus.color} ${tierStatus.border}`}>
                              {tierStatus.label}
                            </span>
                            {isActive && (
                              <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-slate-400 text-xs">{tier.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="divide-y divide-slate-50">
                      {tier.docs.map((doc) => {
                        const docStatus = statusConfig[doc.status];
                        return (
                          <div key={doc.name} className="flex items-center gap-3 px-4 py-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                              <i className={`${doc.icon} text-slate-400 text-sm`} />
                            </div>
                            <p className="text-slate-600 text-sm flex-1">{doc.name}</p>
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${docStatus.bg} ${docStatus.border}`}>
                              <i className={`${docStatus.icon} text-xs ${docStatus.color}`} />
                              <span className={`text-xs font-medium ${docStatus.color}`}>{docStatus.label}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Upgrade CTA for next tier */}
                    {tier.level === currentTier + 1 && (
                      <div className="px-4 pb-4 pt-2">
                        <button
                          onClick={() => setShowUpgrade(true)}
                          className="w-full py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold cursor-pointer hover:bg-emerald-700 transition-colors whitespace-nowrap"
                        >
                          Upgrade to Tier 3
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              <p className="text-slate-400 text-xs text-center px-4 pb-2">
                KYC verification is powered by our compliance partner. Documents are encrypted and stored securely.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Limits table */}
              {tiers.map((tier) => {
                const isCurrentOrBelow = tier.level <= currentTier;
                return (
                  <div
                    key={tier.level}
                    className={`rounded-2xl border px-4 py-4 ${
                      tier.level === currentTier
                        ? "border-emerald-200 bg-emerald-50/50"
                        : "border-slate-100 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <p className="text-slate-800 text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {tier.name} — {tier.label}
                        </p>
                        {tier.level === currentTier && (
                          <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      {isCurrentOrBelow ? (
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />
                      ) : (
                        <i className="ri-lock-line text-slate-300 text-lg" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5">
                        <p className="text-slate-400 text-xs mb-0.5">Daily Limit</p>
                        <p className={`text-sm font-bold ${isCurrentOrBelow ? "text-slate-800" : "text-slate-300"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {tier.dailyLimit}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-100 px-3 py-2.5">
                        <p className="text-slate-400 text-xs mb-0.5">Monthly Limit</p>
                        <p className={`text-sm font-bold ${isCurrentOrBelow ? "text-slate-800" : "text-slate-300"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {tier.monthlyLimit}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Info note */}
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3.5">
                <i className="ri-information-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
                <p className="text-slate-600 text-xs leading-relaxed">
                  Limits apply to total buy/sell and withdrawal transactions per period. Crypto deposits have no limits. Upgrade to Tier 3 for higher limits.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Tier 3 Upgrade Sub-Sheet ── */}
      {showUpgrade && (
        <div className="absolute inset-0 z-10 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={resetUpgrade} />
          <div
            className="relative bg-white rounded-t-3xl w-full max-h-[92vh] flex flex-col"
            style={{ animation: "slideUp 0.25s ease-out" }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-slate-200 rounded-full" />
            </div>

            {/* Upgrade Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={submitStatus === "success" ? resetUpgrade : upgradeStep > 1 ? () => setUpgradeStep(s => s - 1) : resetUpgrade}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
                >
                  <i className="ri-arrow-left-line text-slate-600 text-lg" />
                </button>
                <div>
                  <h2 className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {submitStatus === "success" ? "Application Submitted" : "Upgrade to Tier 3"}
                  </h2>
                  {submitStatus !== "success" && (
                    <p className="text-slate-400 text-xs">Step {upgradeStep} of 3</p>
                  )}
                </div>
              </div>
              {submitStatus !== "success" && (
                <div className="flex items-center gap-1">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`h-1.5 rounded-full transition-all ${s <= upgradeStep ? "bg-emerald-500 w-6" : "bg-slate-200 w-3"}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Upgrade Content */}
            <div className="overflow-y-auto flex-1 px-5 py-5 pb-8">
              {submitStatus === "success" ? (
                /* ── Success State ── */
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-4xl" />
                  </div>
                  <h3 className="text-slate-800 font-bold text-xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Application Received!
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 max-w-xs">
                    Your Tier 3 upgrade request has been submitted. Our compliance team will review your documents within <strong>2–5 business days</strong>.
                  </p>
                  <div className="w-full bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3.5 mb-6 text-left">
                    <div className="flex items-start gap-3">
                      <i className="ri-time-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-700 text-sm font-semibold mb-0.5">What happens next?</p>
                        <ul className="text-slate-500 text-xs space-y-1 leading-relaxed">
                          <li>• You&apos;ll receive an email confirmation shortly</li>
                          <li>• Our team reviews your documents (2–5 days)</li>
                          <li>• You&apos;ll be notified once Tier 3 is activated</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={resetUpgrade}
                    className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold cursor-pointer hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  >
                    Back to KYC Status
                  </button>
                </div>
              ) : submitStatus === "error" ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-5">
                    <i className="ri-close-circle-fill text-red-400 text-4xl" />
                  </div>
                  <h3 className="text-slate-800 font-bold text-xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Submission Failed</h3>
                  <p className="text-slate-500 text-sm mb-6">Something went wrong. Please try again.</p>
                  <button
                    onClick={() => setSubmitStatus("idle")}
                    className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold cursor-pointer hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  >
                    Try Again
                  </button>
                </div>
              ) : upgradeStep === 1 ? (
                /* ── Step 1: Utility Bill ── */
                <form data-readdy-form className="space-y-5">
                  <div className="bg-slate-50 rounded-2xl px-4 py-4 border border-slate-100">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-emerald-600 text-base" />
                      </div>
                      <div>
                        <p className="text-slate-800 text-sm font-semibold">Utility Bill</p>
                        <p className="text-slate-400 text-xs mt-0.5">Must be dated within the last 3 months. Accepted: electricity, water, internet, or gas bill.</p>
                      </div>
                    </div>
                  </div>

                  {/* Upload area */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${utilityBillFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"}`}
                  >
                    {utilityBillPreview ? (
                      <img src={utilityBillPreview} alt="Utility bill preview" className="max-h-40 rounded-xl object-contain mb-3" />
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                        <i className={`${utilityBillFile ? "ri-file-check-line text-emerald-500" : "ri-upload-cloud-2-line text-slate-400"} text-2xl`} />
                      </div>
                    )}
                    {utilityBillFile ? (
                      <div className="text-center">
                        <p className="text-emerald-600 text-sm font-semibold">{utilityBillFile.name}</p>
                        <p className="text-slate-400 text-xs mt-0.5">{(utilityBillFile.size / 1024).toFixed(0)} KB · Tap to change</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-slate-700 text-sm font-semibold">Tap to upload</p>
                        <p className="text-slate-400 text-xs mt-0.5">JPG, PNG or PDF · Max 10MB</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="utility_bill_file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>

                  <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-3">
                    <i className="ri-shield-keyhole-line text-blue-400 text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-slate-500 text-xs leading-relaxed">Your document is encrypted and only accessible to our compliance team. We never share your data.</p>
                  </div>

                  <button
                    type="button"
                    disabled={!canProceedStep1}
                    onClick={() => setUpgradeStep(2)}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${canProceedStep1 ? "bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                  >
                    Continue
                  </button>
                </form>
              ) : upgradeStep === 2 ? (
                /* ── Step 2: Source of Funds ── */
                <form data-readdy-form className="space-y-4">
                  <div className="bg-slate-50 rounded-2xl px-4 py-4 border border-slate-100">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-emerald-600 text-base" />
                      </div>
                      <div>
                        <p className="text-slate-800 text-sm font-semibold">Source of Funds Declaration</p>
                        <p className="text-slate-400 text-xs mt-0.5">Required by financial regulations. All information is kept strictly confidential.</p>
                      </div>
                    </div>
                  </div>

                  {/* Employment type */}
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-2">Employment Type <span className="text-red-400">*</span></label>
                    <div className="grid grid-cols-2 gap-2">
                      {["Employed", "Self-Employed", "Business Owner", "Student", "Retired", "Other"].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setEmploymentType(type)}
                          className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${employmentType === type ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Employer / Business name */}
                  {(employmentType === "Employed" || employmentType === "Business Owner") && (
                    <div>
                      <label className="block text-slate-700 text-sm font-semibold mb-2">
                        {employmentType === "Employed" ? "Employer Name" : "Business Name"}
                      </label>
                      <input
                        type="text"
                        name="employer"
                        value={employer}
                        onChange={(e) => setEmployer(e.target.value)}
                        placeholder={employmentType === "Employed" ? "e.g. Dangote Group" : "e.g. Acme Ltd"}
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-emerald-400 transition-colors"
                      />
                    </div>
                  )}

                  {/* Monthly income range */}
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-2">Monthly Income Range <span className="text-red-400">*</span></label>
                    <select
                      name="monthly_income"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-emerald-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select range</option>
                      <option value="below_100k">Below ₦100,000</option>
                      <option value="100k_500k">₦100,000 – ₦500,000</option>
                      <option value="500k_1m">₦500,000 – ₦1,000,000</option>
                      <option value="1m_5m">₦1,000,000 – ₦5,000,000</option>
                      <option value="above_5m">Above ₦5,000,000</option>
                    </select>
                  </div>

                  {/* Source of funds description */}
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-2">
                      Describe Your Source of Funds <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="source_of_funds"
                      value={sourceOfFunds}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) setSourceOfFunds(e.target.value);
                      }}
                      placeholder="e.g. Monthly salary from employment, rental income from property, proceeds from business operations..."
                      rows={4}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${sourceOfFunds.length >= 490 ? "text-red-400" : "text-slate-400"}`}>
                      {sourceOfFunds.length}/500
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={!canProceedStep2}
                    onClick={() => setUpgradeStep(3)}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${canProceedStep2 ? "bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                  >
                    Continue
                  </button>
                </form>
              ) : (
                /* ── Step 3: Review & Submit ── */
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm">Review your submission before sending. Once submitted, our team will verify within 2–5 business days.</p>

                  {/* Review cards */}
                  <div className="rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                      <p className="text-slate-700 text-sm font-semibold">Documents</p>
                    </div>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-check-line text-emerald-500 text-sm" />
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-700 text-sm font-medium">Utility Bill</p>
                        <p className="text-slate-400 text-xs">{utilityBillFile?.name}</p>
                      </div>
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                      <p className="text-slate-700 text-sm font-semibold">Source of Funds</p>
                    </div>
                    <div className="divide-y divide-slate-50">
                      <div className="px-4 py-3 flex items-center justify-between">
                        <p className="text-slate-500 text-sm">Employment Type</p>
                        <p className="text-slate-800 text-sm font-semibold">{employmentType}</p>
                      </div>
                      {employer && (
                        <div className="px-4 py-3 flex items-center justify-between">
                          <p className="text-slate-500 text-sm">Employer / Business</p>
                          <p className="text-slate-800 text-sm font-semibold">{employer}</p>
                        </div>
                      )}
                      <div className="px-4 py-3 flex items-center justify-between">
                        <p className="text-slate-500 text-sm">Monthly Income</p>
                        <p className="text-slate-800 text-sm font-semibold">
                          {monthlyIncome === "below_100k" ? "Below ₦100k"
                            : monthlyIncome === "100k_500k" ? "₦100k – ₦500k"
                            : monthlyIncome === "500k_1m" ? "₦500k – ₦1M"
                            : monthlyIncome === "1m_5m" ? "₦1M – ₦5M"
                            : "Above ₦5M"}
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-slate-500 text-sm mb-1">Source Description</p>
                        <p className="text-slate-700 text-sm leading-relaxed">{sourceOfFunds}</p>
                      </div>
                    </div>
                  </div>

                  {/* Consent */}
                  <div className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3.5">
                    <i className="ri-shield-check-line text-emerald-500 text-base mt-0.5 flex-shrink-0" />
                    <p className="text-slate-500 text-xs leading-relaxed">
                      By submitting, I confirm that all information provided is accurate and I consent to our compliance team reviewing my documents for KYC purposes.
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={submitStatus === "submitting"}
                    onClick={handleUpgradeSubmit}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${submitStatus === "submitting" ? "bg-emerald-400 text-white cursor-not-allowed" : "bg-emerald-600 text-white cursor-pointer hover:bg-emerald-700"}`}
                  >
                    {submitStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Upgrade Request"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
